/**
 * Multi-file upload classification for the viewer's Config panel
 * (MAD-1538).
 *
 * An uploaded **IR** alone carries no `plan.json` + `config_hash`
 * sidecars, so large-N runs fall back to the raw-outcome path and the
 * attribution panels (bonus split, per-symbol, per-rule) render blank
 * placeholders. Those two sidecars are derived from the GameAST, not
 * the IR — `config_hash` is the SHA-256 of the canonical GameAST and
 * the plan embeds `game_ast` + `rule_slots`, neither of which the IR
 * contains — so they can't be recomputed in-browser without the full
 * DSL→IR port (MAD-1537). Until that lands, the user supplies them by
 * dropping the sibling files alongside the IR.
 *
 * This module is the pure classifier: given the raw text of one or
 * more uploaded files, it sorts them into the IR, the plan JSON, and
 * the config hash, validating consistency. The App wires it to the
 * file input; the split keeps the (untestable-in-jsdom) File reading
 * out of the (heavily-tested) classification logic.
 *
 * Accepted files (classified by content, not filename):
 * - **game source** (spec 016 US4): a raw DSL `game.json` — a JSON object with
 *   `states` and `symbols` and neither `entry_state` nor `game_ast`. The App
 *   compiles it in-browser via `compile_game` (MAD-1537/1924), which produces
 *   the IR + plan + config_hash itself, so it must be dropped **alone** (mixing
 *   it with a pre-built IR/plan/hash is rejected as ambiguous). The matching
 *   `game.layout.json` sidecar — if also dropped — is ignored for semantics
 *   (Constitution V); it carries only visual-editor layout.
 * - **IR**: a JSON object with an `entry_state` key and no `game_ast`
 *   (the compiled execution form the WGSL codegen consumes).
 * - **plan**: a JSON object with both `game_ast` and a string
 *   `config_hash` (the Rust wire-format `ExecutionPlan`). Its embedded
 *   `config_hash` is used unless a standalone hash file overrides it,
 *   so dropping IR + plan is enough — no separate hash file needed.
 * - **config_hash**: a plain-text file holding a 64-char lowercase hex
 *   string (optional; only needed when no plan is supplied).
 */

export interface UploadFile {
  /** Original filename — used only for error messages. */
  name: string;
  /** Raw file contents. */
  text: string;
}

export interface ClassifiedUpload {
  /**
   * Raw DSL `game.json` source text + filename, or null. When present the
   * caller compiles it via `compile_game` (and the IR/plan/hash fields are
   * always null — a game source is dropped alone). Spec 016 US4.
   */
  gameSource: { text: string; label: string } | null;
  /** Parsed IR object + its source filename, or null if none supplied. */
  ir: { json: Record<string, unknown>; label: string } | null;
  /** Verbatim plan JSON text (passed unchanged to WASM), or null. */
  planJson: string | null;
  /** Resolved config hash (from a standalone file or the plan), or null. */
  configHash: string | null;
  /**
   * Fatal classification problems. When non-empty the caller must
   * reject the upload and leave existing state untouched.
   */
  errors: string[];
}

const HEX64 = /^[0-9a-f]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Sort uploaded files into IR / plan / config-hash. See module doc for
 * the per-kind discriminators. Pure and synchronous: the caller reads
 * the `File` contents to text first.
 */
export function classifyUploads(
  files: readonly UploadFile[],
): ClassifiedUpload {
  const errors: string[] = [];
  let gameSource: ClassifiedUpload["gameSource"] = null;
  let ir: ClassifiedUpload["ir"] = null;
  let planJson: string | null = null;
  let planHash: string | null = null;
  let standaloneHash: string | null = null;

  for (const file of files) {
    const trimmed = file.text.trim();
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      // Not JSON — only a bare config-hash file is acceptable here.
      if (HEX64.test(trimmed)) {
        if (standaloneHash !== null) {
          errors.push("multiple config-hash files uploaded; supply only one");
        } else {
          standaloneHash = trimmed;
        }
      } else {
        errors.push(
          `unrecognized file "${file.name}" (not valid JSON or a 64-hex config hash)`,
        );
      }
      continue;
    }

    if (!isRecord(parsed)) {
      errors.push(`unrecognized file "${file.name}" (expected a JSON object)`);
      continue;
    }

    const hasGameAst = "game_ast" in parsed;
    const hasEntryState = "entry_state" in parsed;

    if (hasGameAst && typeof parsed.config_hash === "string") {
      // Plan: carries game_ast + the canonical-GameAST config hash.
      if (planJson !== null) {
        errors.push("multiple plan files uploaded; supply only one");
        continue;
      }
      const embedded = parsed.config_hash.trim();
      if (!HEX64.test(embedded)) {
        errors.push(`plan "${file.name}" has a malformed config_hash`);
        continue;
      }
      planJson = trimmed;
      planHash = embedded;
    } else if (hasEntryState && !hasGameAst) {
      // IR: the compiled execution form.
      if (ir !== null) {
        errors.push("multiple IR files uploaded; supply only one");
        continue;
      }
      ir = { json: parsed, label: file.name };
    } else if ("states" in parsed && "symbols" in parsed) {
      // Raw DSL game.json: the App compiles it via `compile_game`. (Reached
      // only when it is neither an IR (no `entry_state`) nor a plan (no
      // `game_ast`) — both checked above.) A `game.layout.json` sidecar also
      // has neither key but lacks `states`/`symbols`, so it lands in the
      // unrecognized branch below and is harmlessly rejected if dropped solo;
      // when dropped alongside the game.json the user simply omits it.
      if (gameSource !== null) {
        errors.push("multiple game.json files uploaded; supply only one");
        continue;
      }
      gameSource = { text: trimmed, label: file.name };
    } else {
      errors.push(
        `unrecognized JSON file "${file.name}" (not a game.json, IR, or plan)`,
      );
    }
  }

  // A raw game.json compiles to its own IR + plan + config_hash, so mixing it
  // with a pre-built IR/plan/hash upload is ambiguous — reject rather than
  // silently pick one.
  if (
    gameSource !== null &&
    (ir !== null || planJson !== null || standaloneHash !== null)
  ) {
    errors.push(
      "drop a raw game.json on its own — it compiles to the IR + plan in-browser; " +
        "don't mix it with an IR/plan/config-hash upload",
    );
  }

  // A standalone hash file overrides the plan's embedded hash, but a
  // mismatch is almost certainly a mistake (wrong pair of files).
  if (
    standaloneHash !== null &&
    planHash !== null &&
    standaloneHash !== planHash
  ) {
    errors.push(
      "config-hash file disagrees with the plan's embedded config_hash",
    );
  }
  const configHash = standaloneHash ?? planHash;

  return { gameSource, ir, planJson, configHash, errors };
}
